import {
	type NavigateFunction,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import type { ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import {
	type FwQueryBrowserController,
	FwQueryBrowserState,
	type FwQueryFilter,
	useFwQueryBrowserState,
} from "../lib/FwReact/FwQuery";
import { createStore, type Store, useStore } from "./store";
import { type AppConfigState, useAppConfigState } from "./useAppConfigState";
import { type Catalog, CatalogState, useCatalogState } from "./useCatalogState";
import {
	type CheckoutFormState,
	useCheckoutFormState,
	type Warehouse,
	type WarehouseLocation,
} from "./useCheckoutFormState";
import { type SiteLayoutState, useSiteLayoutState } from "./useSiteLayoutState";
import {
	type StorefrontSettingsState,
	useStorefrontSettingsState,
} from "./useStorefrontSettingsState";

export type ProductCategoriesTree = {
	Departments?: ProductDepartment[];
};

export type ProductDepartment = {
	Id: string;
	Name: string;
	UrlName?: string;
	//Count?: number;
	Categories?: ProductCategory[];
	hasExpander?: boolean;
};

export type ProductCategory = {
	Id: string;
	Name: string;
	UrlName: string;
	LinkUrl: string;
	//Count: number;
	SubCategories?: ProductSubCategory[];
};

export type ProductSubCategory = {
	Id: string;
	Name: string;
	UrlName: string;
	LinkUrl: string;
	//Count: number;
};

export class ProductAvailability {
	TotalIn: number = 0;
	Total: number = 0;
}

export type Product = {
	Id: string;
	WebCatalogId?: string;
	WebCatalog?: string;
	InventoryDepartmentId?: string;
	InventoryDepartment?: string;
	CategoryId?: string;
	Category?: string;
	SubCategoryId?: string;
	SubCategory?: string;
	InventoryId: string;
	InventoryNo: string;
	ItemName?: string;
	ItemUrlName?: string;
	Images?: ProductImage[];
	DetailedDescription?: string;
	TechnicalNotes?: string;
	Keywords?: string;
	Tags?: string;
	Barcodes?: string;

	// details
	Availability?: ProductAvailability;
	DailyRate: number;
	WeeklyRate: number;
	MonthlyRate: number;
	SalesPrice: number;
	CurrencySymbol: string;
	CurrencyCode: string;
	Manufacturer: string;
	ManufacturerPartNumber: string;
	ManufacturerUrl: string;
	UnitValue: number;
	ReplacementCost: number;
	ShipWeightLbs: number;
	ShipWeightOz: number;

	PackageItems?: PackageItem[];
	PackagePrice: "CP" | "IP" | "SP";
};

export type PackageItem = {
	Images?: ProductImage[];
	Charge: boolean;
	DefaultQuantity: number;
	DefaultColor: string;
	Description: string;
	ICode: string;
	//ICodeColor: string;
	InventoryId: string;
	InventoryPackageInventoryId: string;
	IsOption: boolean;
	IsPrimary: boolean;
	IsRequired: boolean;
	//LineColor: string;
	NoChargePrint: false;
	ProductId: number;
	OptionColor: string;
	ItemColor: string;
	DailyRate: number;
	WeeklyRate: number;
	MonthlyRate: number;
	DailyExtended: number;
	WeeklyExtended: number;
	MonthlyExtended: number;
	SalesPrice: number;
	CurrencyId: string;
	CurrencyCode: string;
	CurrencySymbol: string;
	Availability?: ProductAvailability;
};

export type ProductImage = {
	Id: string;
	Url: string;
	//Thumbnail: string
	//Image: string
};

export type ProductsFilter = {
	catalog?: Catalog | ImmutableObject<Catalog>;
	department?: ProductDepartment;
	category?: ProductCategory;
	subCategory?: ProductSubCategory;
	searchText?: string;
	warehouse?: Warehouse;
	location?: WarehouseLocation;
	showLandingPage: boolean;
};

export class InventoryModel {
	productCategoriesTree?: ProductCategoriesTree;

	catalog?: Catalog | ImmutableObject<Catalog>;
	department?: ProductDepartment;
	category?: ProductCategory;
	subCategory?: ProductSubCategory;
	location?: WarehouseLocation | ImmutableObject<WarehouseLocation>;
	warehouse?: Warehouse | ImmutableObject<Warehouse>;

	stopLoadingCategoryTree: boolean = false;
	loadingCategoryTree: boolean = false;
	loadingCategoryTreeAbortController?: AbortController;

	stopLoadingCatalog: boolean = false;
	loadingCatalog: boolean = false;
	loadingCatalogId?: string;
	loadingCatalogAbortController?: AbortController;

	showLandingPage: boolean = true;
	sidebarExpanded: boolean = false;
}

// global hookstates
const inventoryPageStateGlobalStore = createStore<InventoryModel>(
	new InventoryModel(),
);
const productBrowserStateGlobalStore = createStore<
	FwQueryBrowserState<Product>
>(new FwQueryBrowserState<Product>());

export const ensureDateTimeSegment = (
	dateTime: string | undefined,
	date: string,
	time: string,
): string => {
	if (dateTime && /[tT]\d{2}:\d{2}(?::\d{2})?$/.test(dateTime)) {
		return dateTime;
	} else if (date) {
		if (time) {
			return `${date}t${time}`;
		}
		return date;
	}
	return "";
	//return value || '';
	// if (!value) {
	// 	return `${fallbackDate}t${fallbackTime}`;
	// }
	// if (/[tT]\d{2}:\d{2}(?::\d{2})?$/.test(value)) {
	// 	return value;
	// }
	// if (value) {
	// 	if (fallbackTime) {
	// 		return `${value}t${fallbackTime}`;
	// 	}
	// 	return value;
	// }
	// return '';
};

export class InventoryState {
	private appConfigState: AppConfigState;
	private catalogState: CatalogState;
	private inventoryState: Store<InventoryModel>;
	private productsQueryState: FwQueryBrowserController<Product>;
	private siteLayoutState: SiteLayoutState;
	private navigate: NavigateFunction;
	private checkoutFormState: CheckoutFormState;
	private storefrontSettingsState: StorefrontSettingsState;
	private keywords: string;

	constructor(
		appConfigState: AppConfigState,
		catalogState: CatalogState,
		inventoryState: Store<InventoryModel>,
		productsQueryState: FwQueryBrowserController<Product>,
		siteLayoutState: SiteLayoutState,
		navigate: NavigateFunction,
		checkoutFormState: CheckoutFormState,
		storefrontSettingsState: StorefrontSettingsState,
		keywords: string,
	) {
		this.appConfigState = appConfigState;
		this.catalogState = catalogState;
		this.inventoryState = inventoryState;
		this.productsQueryState = productsQueryState;
		this.siteLayoutState = siteLayoutState;
		this.navigate = navigate;
		this.checkoutFormState = checkoutFormState;
		this.storefrontSettingsState = storefrontSettingsState;
		this.keywords = keywords;
	}

	get(): ImmutableObject<InventoryModel> {
		return this.inventoryState.getState();
	}

	getProductsState(): FwQueryBrowserController<Product> {
		return this.productsQueryState;
	}

	setSidebarExpanded(value: boolean) {
		this.inventoryState.setState((prev) => ({
			...prev,
			sidebarExpanded: value,
		}));
	}

	async clearProductsAsync() {
		const newProductCategoriesTreeState: Partial<ProductCategoriesTree> = {};
		newProductCategoriesTreeState.Departments = undefined;
		this.inventoryState.setState((prev) => ({
			...prev,
			productCategoriesTree: {
				...prev.productCategoriesTree,
				...newProductCategoriesTreeState,
			},
		}));
		await this.productsQueryState.clearAsync();
	}

	// Add this method to clear the category tree cache
	async clearCategoryTreeAsync(): Promise<void> {
		// console.log('🧹 Clearing category tree cache');
		this.inventoryState.setState((prev) => ({
			...prev,
			productCategoriesTree: undefined,
			stopLoadingCategoryTree: false,
		}));
	}

	// Update loadProductCategoryTreeAsync to always reload
	async loadProductCategoryTreeAsync(catalogid: string): Promise<void> {
		if (
			this.appConfigState.get().appConfig &&
			!this.inventoryState.getState().loadingCategoryTree
		) {
			try {
				this.inventoryState.setState((prev) => ({
					...prev,
					loadingCategoryTree: true,
				}));
				// console.log('🌳 Loading category tree for catalog:', catalogid);
				const axiosCategoryTreeResponse =
					await storefrontSdk.getCategoryTreeAsync(catalogid);
				this.inventoryState.setState((prev) => ({
					...prev,
					productCategoriesTree:
						axiosCategoryTreeResponse.data as ProductCategoriesTree,
				}));
			} catch (error) {
				this.inventoryState.setState((prev) => ({
					...prev,
					stopLoadingCategoryTree: true,
				}));
				this.siteLayoutState.handleErrorAsync(error);
			} finally {
				this.inventoryState.setState((prev) => ({
					...prev,
					loadingCategoryTree: false,
				}));
			}
		}
	}

	async getCatalogAsync(catalogid: string): Promise<Catalog | undefined> {
		if (
			this.appConfigState.get().appConfig &&
			!this.inventoryState.getState().loadingCatalog &&
			!this.inventoryState.getState().stopLoadingCatalog
		) {
			try {
				this.inventoryState.setState((prev) => ({
					...prev,
					loadingCatalog: true,
				}));
				const axiosCatalogResponse =
					await storefrontSdk.getCatalogByIdAsync(catalogid);
				const catalog = (axiosCatalogResponse.data?.[0] ??
					undefined) as unknown as Catalog | undefined;
				if (!catalog) {
					return undefined;
				}
				CatalogState.addFieldIndexesToCatalog(catalog);
				return catalog;
			} catch (error) {
				this.inventoryState.setState((prev) => ({
					...prev,
					stopLoadingCatalog: true,
				}));
				this.siteLayoutState.handleErrorAsync(error);
			} finally {
				this.inventoryState.setState((prev) => ({
					...prev,
					loadingCatalog: false,
				}));
			}
		}
		return;
	}

	async getCatalogByIdAsync(
		catalogId: string,
	): Promise<Catalog | ImmutableObject<Catalog> | undefined> {
		//if (!this.catalogState.get().catalogs) {
		//    await this.catalogState.loadAsync();
		//}
		const catalogs = this.catalogState.get().catalogs ?? [];
		if (catalogs) {
			for (let i = 0; i < (catalogs.length ?? 0); i++) {
				const catalog = catalogs[i];
				if (catalog.Id === catalogId) {
					return catalog;
				}
			}
		}
		return;
	}

	async getDepartmentByIdAsync(
		catalogId: string,
		departmentId: string,
	): Promise<ProductDepartment | undefined> {
		if (!this.inventoryState.getState().productCategoriesTree) {
			await this.loadProductCategoryTreeAsync(catalogId);
		}
		if (this.inventoryState.getState().productCategoriesTree) {
			const departments =
				this.inventoryState.getState().productCategoriesTree?.Departments ?? [];
			for (let i = 0; i < departments.length; i++) {
				const department = departments[i];
				if (department.Id === departmentId) {
					return department as ProductDepartment;
				}
			}
		}
		return;
	}

	async getCategoryByIdAsync(
		catalogId: string,
		_departmentId: string,
		categoryId: string,
	): Promise<ProductCategory | undefined> {
		if (!this.inventoryState.getState().productCategoriesTree) {
			await this.loadProductCategoryTreeAsync(catalogId);
		}
		if (this.inventoryState.getState().productCategoriesTree) {
			const departments =
				this.inventoryState.getState().productCategoriesTree?.Departments ?? [];
			for (let i = 0; i < departments.length; i++) {
				const department = departments[i];
				if (department.Categories) {
					for (let j = 0; j < department.Categories.length; j++) {
						const category = department.Categories[j];
						if (category.Id === categoryId) {
							return category as ProductCategory;
						}
					}
				}
			}
		}
		return;
	}

	async getSubCategoryByIdAsync(
		catalogId: string,
		_departmentId: string,
		_categoryId: string,
		subCategoryId: string,
	): Promise<ProductSubCategory | undefined> {
		if (!this.inventoryState.getState().productCategoriesTree) {
			await this.loadProductCategoryTreeAsync(catalogId);
		}
		if (this.inventoryState.getState().productCategoriesTree) {
			const departments =
				this.inventoryState.getState().productCategoriesTree?.Departments ?? [];
			for (let i = 0; i < departments.length; i++) {
				const department = departments[i];
				if (department.Categories) {
					for (let j = 0; j < department.Categories.length; j++) {
						const category = department.Categories[j];
						if (category.SubCategories) {
							for (let k = 0; k < category.SubCategories.length; k++) {
								const subCategory = category.SubCategories[k];
								if (subCategory.Id === subCategoryId) {
									return subCategory as ProductSubCategory;
								}
							}
						}
					}
				}
			}
		}
		return;
	}

	// function: filters the catalog by catalogid
	async filterByCatalogIdAsync(
		warehouse: Warehouse | undefined,
		location: WarehouseLocation | undefined,
		catalogId: string,
		showLandingPage: boolean,
	): Promise<void> {
		//try {
		let catalog: Catalog | ImmutableObject<Catalog> | undefined;
		catalog = {
			Id: catalogId,
			Name: "Loading...",
			UrlName: "",
			Images: "",
			DepartmentId: "",
			LocationId: "",
			WarehouseId: "",
			AllowAddingUnavailableItems: false,
			WarehousesQtys: [],
		};
		if (catalogId) {
			if (
				!this.inventoryState.getState().catalog ||
				(this.inventoryState.getState().catalog?.Id ?? "") === "" ||
				(this.inventoryState.getState().catalog?.Id ?? "") !== catalogId
			) {
				catalog = await this.getCatalogAsync(catalogId);
				//this.inventoryState.catalog.set(catalog);
			} else {
				catalog = this.inventoryState.getState().catalog;
			}
			this.checkoutFormState.updateFromDate(catalogId);
			this.checkoutFormState.updateToDate(catalogId);
		}
		await this.setProductFilterAsync({
			warehouse: warehouse,
			location: location,
			catalog: catalog,
			department: undefined,
			category: undefined,
			subCategory: undefined,
			searchText: "", // clear the searchText
			showLandingPage: showLandingPage,
		});
		//} catch (error) {
		//   this.siteLayoutState.showError(error);
		//}
	}

	// function: filters the catalog by catalogid
	//async filterByCatalogAsync(warehouse: Warehouse, location: WarehouseLocation, catalog: Catalog, showLandingPage: boolean): Promise<void> {
	//    try {
	//        //let catalog: Catalog | undefined;
	//        //if (catalogId) {
	//        //    let result = await this.getCatalogAsync(catalogId);
	//        //    //console.log(result);
	//        //    catalog = result
	//        //}
	//        this.inventoryState.catalog.set(catalog);
	//        await this.setProductFilterAsync({
	//            warehouse: warehouse,
	//            location: location,
	//            catalog: catalog,
	//            department: undefined,
	//            category: undefined,
	//            subCategory: undefined,
	//            searchText: '', // clear the searchText
	//            showLandingPage: showLandingPage
	//        });
	//    } catch (error) {
	//        this.siteLayoutState.showError(error);
	//    }
	//}

	// Add method to clear search text when category tree is used
	async clearSearchTextAsync(): Promise<void> {
		// console.log('🧹 Clearing search text for category tree navigation');
		// console.log('🧹 Current catalog:', this.inventoryState.getState().catalog);
		// console.log('🧹 Current searchText:', this.inventoryState.getState().searchText);
		await this.setProductFilterAsync({
			warehouse: this.inventoryState.getState().warehouse,
			location: this.inventoryState.getState().location,
			catalog: this.inventoryState.getState().catalog,
			department: this.inventoryState.getState().department,
			category: this.inventoryState.getState().category,
			subCategory: this.inventoryState.getState().subCategory,
			searchText: "", // Clear the search text
			showLandingPage: this.inventoryState.getState().showLandingPage,
		});
		// console.log('🧹 After clearSearchTextAsync, searchText is now:', this.inventoryState.getState().searchText);
	}

	async clearCatalogFilterAsync(catalogid?: string) {
		const newInventoryState: Partial<InventoryModel> = {};
		newInventoryState.catalog = undefined;
		if (catalogid !== undefined) {
			const catalogs = this.catalogState.get().catalogs;
			if (catalogs !== undefined && catalogs.length > 0) {
				const catalog = catalogs.find((c) => c.Id === catalogid);
				if (catalog !== undefined) {
					newInventoryState.catalog = catalog;
				}
			}
		}
		newInventoryState.showLandingPage = true;
		this.inventoryState.setState((prev) => ({ ...prev, ...newInventoryState }));

		await this.productsQueryState.clearAsync();
	}

	selectDefaultCatalog() {
		//console.log('selectDefaultCatalog');
		const catalogs = this.catalogState.get().catalogs;
		if (catalogs !== undefined && catalogs.length > 0) {
			this.navigate(`/inventory/${catalogs[0].Id}`);
		}
	}

	async searchAsync(catalogId: string, searchText: string, pageNo: number) {
		const filters: FwQueryFilter[] = [];
		if (searchText && searchText.length > 0) {
			filters.push({ Field: "Keywords", Op: "contains", Value: searchText });
		}
		await this.productsQueryState.setFiltersAsync(filters);
		await this.productsQueryState.setPageNoAsync(pageNo, false, true);
		const catalog = this.inventoryState.getState().catalog;
		if (!catalog) {
			await this.filterByCatalogIdAsync(undefined, undefined, catalogId, false);
		}
		if (catalog) {
			this.inventoryState.setState((prev) => ({
				...prev,
				department: undefined,
				category: undefined,
				subCategory: undefined,
				showLandingPage: false,
			}));
			await this.productsQueryState.loadAsync();
		}
	}

	// setProductFilterAsync
	async setProductFilterAsync(options: ProductsFilter): Promise<void> {
		const newInventoryState: Partial<InventoryModel> = {};
		// filter by catalog
		const catalogidChanged =
			this.inventoryState.getState().catalog?.Id !== options.catalog?.Id;
		const departmentidChanged =
			this.inventoryState.getState().department?.Id !== options.department?.Id;
		const categoryidChanged =
			this.inventoryState.getState().category?.Id !== options.category?.Id;
		const subcategoryidChanged =
			this.inventoryState.getState().subCategory?.Id !==
			options.subCategory?.Id;
		const searchtextChanged = this.keywords !== options.searchText;
		const warehouseidChanged =
			this.inventoryState.getState().warehouse?.WarehouseId !==
			options.warehouse?.WarehouseId;
		const locationidChanged =
			this.inventoryState.getState().location?.LocationId !==
			options.location?.LocationId;
		const showLandingPageChanged =
			this.inventoryState.getState()?.showLandingPage !==
			options.showLandingPage;
		const filterChanged =
			catalogidChanged ||
			departmentidChanged ||
			categoryidChanged ||
			subcategoryidChanged ||
			searchtextChanged ||
			warehouseidChanged ||
			locationidChanged ||
			showLandingPageChanged;
		const hasCategory = options.category !== undefined;
		const hasSubCategories =
			hasCategory &&
			options.category?.SubCategories !== undefined &&
			(options.category?.SubCategories.length ?? 0) > 0;
		const isLoadingCatalog =
			options.catalog?.Id !== undefined && options.department?.Id === undefined;
		const isLoadingDepartment =
			options.department?.Id !== undefined &&
			options.category?.Id === undefined;
		const isLoadingCategoryWithSubCategories =
			hasSubCategories && options.subCategory === undefined;

		if (showLandingPageChanged) {
			newInventoryState.showLandingPage = options.showLandingPage;
		}

		if (options.catalog && filterChanged) {
			document.title = options.catalog.Name;

			// if catalog changed then reload the category tree
			if (
				!this.inventoryState.getState().catalog ||
				this.inventoryState.getState().catalog?.Id !== options.catalog.Id
			) {
			}

			// if the catalog record is not loaded, then load it from the id
			if (!options.catalog.Name) {
				// ajax for the catalog name
				newInventoryState.catalog = await this.getCatalogAsync(
					options.catalog.Id,
				);
			} else {
				newInventoryState.catalog = options.catalog;
			}

			newInventoryState.department = undefined;
			newInventoryState.category = undefined;
			newInventoryState.subCategory = undefined;
			await this.productsQueryState.setPageNoAsync(1, false, true);

			const filters: FwQueryFilter[] = [];

			// filter by department
			if (options.department) {
				newInventoryState.department = options.department;
				newInventoryState.category = undefined;
				newInventoryState.subCategory = undefined;

				if (options.category) {
					newInventoryState.category = options.category;
					newInventoryState.subCategory = undefined;
					if (options.subCategory) {
						newInventoryState.subCategory = options.subCategory;
					}
				}
			}
			if (typeof options.searchText === "string") {
				if (options.searchText.length > 0) {
					filters.push({
						Field: "Keywords",
						Op: "contains",
						Value: options.searchText,
					});
				}
			} else if (this.keywords) {
				filters.push({
					Field: "Keywords",
					Op: "contains",
					Value: this.keywords,
				});
			}
			if (options.warehouse && warehouseidChanged) {
				newInventoryState.warehouse = options.warehouse;
			}
			if (options.location && locationidChanged) {
				newInventoryState.location = options.location;
			}
			if (Object.keys(newInventoryState)) {
				this.inventoryState.setState((prev) => ({
					...prev,
					...newInventoryState,
				}));
			}
			await this.productsQueryState.setFiltersAsync(filters);
			if (!options.showLandingPage) {
				if (
					!isLoadingCatalog &&
					!isLoadingDepartment &&
					!isLoadingCategoryWithSubCategories
				) {
					await this.productsQueryState.loadAsync();
				}
			}
		}
	}

	// setProductFilterAsync
	async setShowLandingPageAsync(value: boolean): Promise<void> {
		this.inventoryState.setState((prev) => ({
			...prev,
			showLandingPage: value,
		}));
	}

	// setProductFilterAsync
	async setCatalog(
		catalog: Catalog | ImmutableObject<Catalog> | undefined,
	): Promise<void> {
		this.inventoryState.setState((prev) => ({ ...prev, catalog }));
	}

	// Update updateSelectedCatalog to handle warehouse/catalog synchronization
	async updateSelectedCatalog(): Promise<void> {
		const storefrontSettings = this.storefrontSettingsState.get().settings;
		const isUniversity = storefrontSettings?.IsUniversity ?? false;
		const filterCatalogsByLocation =
			storefrontSettings?.FilterCatalogsByLocation ?? false;

		// Don't redirect if we're on the home page
		const currentPath = window.location.pathname;
		const isHomePage =
			currentPath === "/" ||
			currentPath === "/storefront" ||
			currentPath === "/storefront/";
		if (isHomePage) {
			return;
		}

		// Handle warehouse/catalog synchronization
		if (filterCatalogsByLocation && !isUniversity) {
			const currentCatalog = this.inventoryState.getState().catalog;
			const warehouseId = this.checkoutFormState.get().warehouseIdValue;

			if (currentCatalog && currentCatalog.WarehouseId !== warehouseId) {
				// Check if there are any catalogs available for the current warehouse
				const availableCatalogs = this.catalogState
					.get()
					.catalogs?.filter((c) => c.WarehouseId === warehouseId);
				if (availableCatalogs && availableCatalogs.length > 0) {
					// Find the first catalog with items in this warehouse
					const newCatalog = availableCatalogs.find((catalog) => {
						const whQty = catalog.WarehousesQtys.find(
							(wq) => wq.WarehouseId === warehouseId && wq.TotalItems > 0,
						);
						return whQty;
					});

					if (newCatalog) {
						// Redirect to a catalog that matches the current warehouse
						await this.clearCategoryTreeAsync();
						this.setProductFilterAsync({
							showLandingPage: true,
							catalog: newCatalog,
						});
						this.navigate(`/inventory/${newCatalog.Id}/index`);
					}
				} else {
					// No catalogs available for current warehouse, update warehouse to match the catalog
					const catalogWarehouse = this.checkoutFormState
						.get()
						.lookupwarehouse?.find(
							(w) => w.WarehouseId === currentCatalog.WarehouseId,
						);
					if (catalogWarehouse) {
						this.checkoutFormState.setWarehouseId(currentCatalog.WarehouseId);

						// Also update the location to the first available location for this warehouse
						const warehouseLocations = this.checkoutFormState
							.get()
							.lookuplocation?.filter(
								(l) => l.WarehouseId === currentCatalog.WarehouseId,
							);
						if (warehouseLocations && warehouseLocations.length > 0) {
							this.checkoutFormState.setLocationId(
								warehouseLocations[0].LocationId,
							);
						}
					}
				}
			} else if (!currentCatalog) {
				// If no catalog is set but we have a catalog ID in the URL, try to set it
				const pathParts = currentPath.split("/");
				const catalogIdIndex = pathParts.indexOf("inventory") + 1;
				if (catalogIdIndex > 0 && catalogIdIndex < pathParts.length) {
					const catalogIdFromUrl = pathParts[catalogIdIndex];
					if (catalogIdFromUrl && catalogIdFromUrl !== "index") {
						const catalogFromUrl = this.catalogState
							.get()
							.catalogs?.find((c) => c.Id === catalogIdFromUrl);
						if (catalogFromUrl) {
							// Set the catalog and update warehouse/location to match
							this.inventoryState.setState((prev) => ({
								...prev,
								catalog: catalogFromUrl,
							}));

							const catalogWarehouse = this.checkoutFormState
								.get()
								.lookupwarehouse?.find(
									(w) => w.WarehouseId === catalogFromUrl.WarehouseId,
								);
							if (catalogWarehouse) {
								this.checkoutFormState.setWarehouseId(
									catalogFromUrl.WarehouseId,
								);

								const warehouseLocations = this.checkoutFormState
									.get()
									.lookuplocation?.filter(
										(l) => l.WarehouseId === catalogFromUrl.WarehouseId,
									);
								if (warehouseLocations && warehouseLocations.length > 0) {
									this.checkoutFormState.setLocationId(
										warehouseLocations[0].LocationId,
									);
								}
							}
						}
					}
				}
			}
		}
	}
}

export function useInventoryState(): InventoryState {
	const appConfigState = useAppConfigState();
	const catalogState = useCatalogState();
	const inventoryState = inventoryPageStateGlobalStore;
	const checkoutFormState = useCheckoutFormState();
	const siteLayoutState = useSiteLayoutState();
	const navigate = useNavigate();
	const storefrontSettingsState = useStorefrontSettingsState();
	let { warehouseid, locationid, fromdate, todate } = useParams();
	const [searchParams] = useSearchParams();
	const keywords = searchParams.get("keywords") || "";

	useStore(inventoryPageStateGlobalStore);
	useStore(productBrowserStateGlobalStore);

	const productBrowserState = useFwQueryBrowserState<Product>(
		productBrowserStateGlobalStore,
		// getUrlAsync
		async () => {
			const catalog = inventoryState.getState().catalog;
			const catalogid = catalog?.Id;
			if (catalog && catalogid) {
				if (appConfigState.get().appConfig) {
					warehouseid = warehouseid ?? checkoutFormState.get().warehouseIdValue;
					locationid = locationid ?? checkoutFormState.get().locationIdValue;
					const fromDateTime = ensureDateTimeSegment(
						fromdate,
						checkoutFormState.get().fromDateValue,
						checkoutFormState.get().fromTimeValue,
					);
					const toDateTime = ensureDateTimeSegment(
						todate,
						checkoutFormState.get().toDateValue,
						checkoutFormState.get().toTimeValue,
					);
					let url1 = "";
					if (catalog.HasRental) {
						url1 = `${appConfigState.get().appConfig?.BaseUrl}/api/v1/storefront/catalog/${catalogid}/products/locationid/${locationid}/warehouseid/${warehouseid}/fromdate/${fromDateTime}/todate/${toDateTime}`;
					}
					if (catalog.HasSales) {
						// no dates for sales catalogs
						url1 = `${appConfigState.get().appConfig?.BaseUrl}/api/v1/storefront/catalog/${catalogid}/products/warehouseid/${warehouseid}/locationid/${locationid}`;
					}
					const url = new URL(url1);
					if (inventoryState.getState().department) {
						const departmentid = inventoryState.getState().department?.Id;
						const filter: string[] = [];
						if (departmentid) {
							filter.push(
								`{Field:"InventoryDepartmentId",Op:"=",Value:"${departmentid}"}`,
							);
							if (inventoryState.getState().category) {
								const categoryid = inventoryState.getState().category?.Id;
								if (categoryid) {
									filter.push(
										`{Field:"CategoryId",Op:"=",Value:"${categoryid}"}`,
									);
									if (inventoryState.getState().subCategory) {
										const subcategoryid =
											inventoryState.getState().subCategory?.Id;
										if (subcategoryid) {
											filter.push(
												`{Field:"SubCategoryId",Op:"=",Value:"${subcategoryid}"}`,
											);
										}
									}
								}
							}
						}
						if (filter.length) {
							url.searchParams.set("filter", `[${filter.join(",")}]`);
						}
					}
					//console.log({url: url.href})
					return url.href;
				}
				return;
			} else {
				return;
			}
		},
	);

	return new InventoryState(
		appConfigState,
		catalogState,
		inventoryState,
		productBrowserState,
		siteLayoutState,
		navigate,
		checkoutFormState,
		storefrontSettingsState,
		keywords || "",
	);
}
